import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { useTranslation } from 'hooks';
import apiUser from 'api/user';
import ModalContent from 'components/modal/components/ModalContent';
import ModalFooter from 'components/modal/components/ModalFooter';
import ModalTitle from 'components/modal/components/ModalTitle';
import Button from 'components/ui/button';
import Loader from 'components/ui/loader';
import { SummaryCard } from 'pages/home/components';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { findContainer } from './helpers';
import { SortableContainer, SortableItem } from './components';
import './homeBlocksSettings.scss';

interface Props {
  callback: (data?: any) => void;
}

export const TotalSummariesSetting: React.FC<Props> = ({ callback }) => {
  const getTranslate = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const { enabled, available } = useSelector(
    (state: RootState) => state.home.todayBlocks
  );
  const [currentActiveId, setCurrentActiveId] = useState<string | null>(null);
  const [items, setItems] = useState({
    enabled,
    available: available.filter(
      ({ id }) => !enabled.some((item) => id === item.id)
    ),
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        tolerance: 5,
        delay: 100,
      },
    })
  );

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setCurrentActiveId(String(active.id));
  }, []);

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const overContainer = findContainer(over.id, items);
      const activeContainer = findContainer(active.id, items);

      if (!overContainer || !activeContainer) {
        return;
      }

      if (activeContainer !== overContainer) {
        setItems((items) => {
          const activeItems = items[activeContainer];
          const overItems = items[overContainer];
          const activeIndex = activeItems.findIndex(
            ({ id }) => id === active.id
          );
          const overIndex = overItems.findIndex(({ id }) => id === over.id);
          const newIndex = overIndex >= 0 ? overIndex : overItems.length + 1;

          return {
            ...items,
            [activeContainer]: activeItems.filter(({ id }) => id !== active.id),
            [overContainer]: [
              ...overItems.slice(0, newIndex),
              activeItems[activeIndex],
              ...overItems.slice(newIndex, overItems.length),
            ],
          };
        });
      }
    },
    [items]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const overContainer = findContainer(over.id, items);
      const activeContainer = findContainer(active.id, items);

      if (!activeContainer || !overContainer) {
        return;
      }

      if (activeContainer === overContainer) {
        setItems((items) => {
          const activeItems = items[activeContainer];
          const overItems = items[overContainer];
          const activeIndex = activeItems.findIndex(
            ({ id }) => id === active.id
          );
          const overIndex = overItems.findIndex(({ id }) => id === over.id);

          return {
            ...items,
            [overContainer]: arrayMove(overItems, activeIndex, overIndex),
          };
        });
      }
    },
    [items]
  );

  const handleSave = async () => {
    setLoading(true);

    try {
      const { getStartedInfoBlocks } = await apiUser.setConfig({
        param: 'getStartedInfoBlocks',
        value: items.enabled,
      });
      setLoading(false);
      callback(getStartedInfoBlocks);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <>
      {isLoading ? <Loader /> : null}

      <ModalTitle>{getTranslate('getStarted.totalSummary.header')}</ModalTitle>
      <ModalContent>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}>
          <div className='flex flex-col gap-4'>
            <div>
              <SortableContainer
                id='enabled'
                items={items.enabled}
                className='grid grid-cols-4 gap-4 grid-rows-[138px] auto-rows-[138px]'>
                {items.enabled.map(({ id }) => (
                  <SortableItem
                    key={id}
                    id={id}
                    disabled={items.enabled.length === 1}>
                    <SummaryCard
                      name={id}
                      isDraggable
                      isFetchDisabled
                      isRedirectDisabled
                      isDragDisabled={items.enabled.length === 1}
                    />
                  </SortableItem>
                ))}
              </SortableContainer>
            </div>
          </div>

          {createPortal(
            <DragOverlay
              modifiers={[snapCenterToCursor]}
              className='drag-overlay__component'>
              {currentActiveId ? (
                <SummaryCard name={currentActiveId} isDraggable isOverlay />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </ModalContent>
      <ModalFooter>
        <Button
          text={getTranslate('common.cancel.button')}
          status='outline'
          size='normal'
          onClick={() => callback()}
        />
        <Button
          text={getTranslate('common.save.button')}
          status='primary'
          size='normal'
          onClick={handleSave}
        />
      </ModalFooter>
    </>
  );
};
